"use client";

import { useEffect, useState } from "react";
import { ACCOUNTS_API_URL } from "@/utils/constants";

const MODULES_URL = `${ACCOUNTS_API_URL}/auth/modules`;
const LOGOUT_URL = `${ACCOUNTS_API_URL}/auth/logout`;
const REQUEST_TIMEOUT = 6000;

// Shared across every consumer on the page so N components cause one network call.
let sessionState = { status: "loading", user: null, apps: {} };
let inFlight = null;
const subscribers = new Set();

function publish(next) {
  sessionState = next;
  subscribers.forEach((notify) => notify(next));
}

function readUser(payload) {
  const user = payload?.data?.user;
  if (!user) return null;

  const parts = [user.firstName, user.lastName].filter(Boolean);
  const name = parts.join(" ").trim();
  if (!name) return null;

  // First letter of the first and last name; single-name users get one letter.
  const initials = parts
    .map((part) => part.trim().charAt(0))
    .join("")
    .toUpperCase();

  return { name, initials };
}

// `isAccessed` is the account app's own definition of "this company has a live
// subscription for this app" - see moduleList() in abss-account-backend.
function readApps(payload) {
  const modules = payload?.data?.modules;
  if (!Array.isArray(modules)) return {};

  return modules.reduce((apps, entry) => {
    if (entry?.module) apps[entry.module] = Boolean(entry.isAccessed);
    return apps;
  }, {});
}

function fetchSession() {
  if (inFlight) return inFlight;

  inFlight = fetch(MODULES_URL, {
    credentials: "include",
    signal: AbortSignal.timeout(REQUEST_TIMEOUT),
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((payload) => {
      const user = readUser(payload);
      publish(
        user
          ? { status: "authenticated", user, apps: readApps(payload) }
          : { status: "anonymous", user: null, apps: {} },
      );
    })
    // Network error, timeout, CORS, expired token - all mean "show the logged out UI".
    .catch(() => publish({ status: "anonymous", user: null, apps: {} }))
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export async function signOut() {
  try {
    await fetch(LOGOUT_URL, {
      method: "POST",
      credentials: "include",
      signal: AbortSignal.timeout(REQUEST_TIMEOUT),
    });
  } catch {
    // Cookies may already be gone; drop the local session either way.
  }
  publish({ status: "anonymous", user: null, apps: {} });
}

/**
 * Reads the shared `.abss.ai` session. Always resolves to "anonymous" rather
 * than throwing, so a failed check degrades to the logged out UI.
 */
export default function useSession() {
  const [state, setState] = useState(sessionState);

  useEffect(() => {
    subscribers.add(setState);
    setState(sessionState);
    if (sessionState.status === "loading") fetchSession();

    return () => subscribers.delete(setState);
  }, []);

  return state;
}
